import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  accountSecurityService,
  AUTH_CONSTANTS,
  withConfirmation as sendWithConfirmation,
} from '@personal-trainer-app/shared';
import { authStore } from '@personal-trainer-app/shared/src/store/auth.store';

import { ConfirmIdentityModal } from '../ConfirmIdentityModal';

import type {
  ConfirmChangeOptions,
  IdentityConfirmationContextValue,
  IdentityConfirmationProviderProps,
  IdentityConfirmationQuestion,
  ResumeChange,
} from './IdentityConfirmationProvider.types';
import type { PendingConfirmation, ResumableChange } from '@/utils';
import type {
  IdentityConfirmation,
  PasswordConfirmationProof,
  SocialProvider,
} from '@personal-trainer-app/shared';

import { savePendingConfirmation, startSocialConfirm } from '@/utils';

const IdentityConfirmationContext =
  createContext<IdentityConfirmationContextValue | null>(null);

const confirmWithPassword = (
  proof: PasswordConfirmationProof
): Promise<IdentityConfirmation> => accountSecurityService.confirm(proof);

const readConfirmation = (): Promise<IdentityConfirmation> =>
  accountSecurityService.getConfirmation();

/**
 * Leave for a provider to confirm it's you: keep the change for the
 * signed-in account, then go. Settings takes it back when the provider
 * returns (7.3).
 */
const saveAndLeave = (pending: PendingConfirmation): void => {
  const username = authStore.getState().user?.username;
  if (username) savePendingConfirmation(username, pending);
  startSocialConfirm(pending.provider);
};

/**
 * IdentityConfirmationProvider — "confirm it's you, then carry on" for
 * everything inside it (social sign-in PRD, Tasks 7.1–7.3). Mounted around
 * Settings, where every change that needs a recent confirmation lives.
 *
 * `useIdentityConfirmation().withConfirmation(change, { label, resume })`
 * sends the change through the shared `withConfirmation`: a 423 opens the one
 * `ConfirmIdentityModal` here, and once the owner has confirmed with the
 * password (and its code, with two-factor on), the change is sent again. Confirming with a provider (while the
 * social sign-in switch is on) leaves the page instead: the change's `resume`
 * is kept for Settings to take back, and resumes through what its kind
 * registered with `useResumableChange`. One question at a time: a newer one
 * replaces one still open.
 */
export function IdentityConfirmationProvider({
  children,
  confirmPassword = confirmWithPassword,
  getConfirmation = readConfirmation,
  showProviders = AUTH_CONSTANTS.SOCIAL_SIGN_IN,
  leaveToConfirm = saveAndLeave,
  onRecoveryCodeSpent,
}: IdentityConfirmationProviderProps): React.JSX.Element {
  // What the modal shows, kept through its exit animation after it closes.
  const [question, setQuestion] = useState<IdentityConfirmationQuestion>({
    label: '',
    methods: [],
    twoFactor: false,
  });
  const [open, setOpen] = useState(false);
  // How the open question is answered: true once confirmed, false if not.
  const answerRef = useRef<((confirmed: boolean) => void) | null>(null);

  const answer = useCallback((confirmed: boolean): void => {
    const settle = answerRef.current;
    answerRef.current = null;
    setOpen(false);
    settle?.(confirmed);
  }, []);

  // A question still open when the page goes counts as backing out.
  useEffect(() => {
    const pending = answerRef;
    return () => pending.current?.(false);
  }, []);

  const withConfirmation = useCallback(
    <T,>(
      change: () => Promise<T>,
      { label, precheck, resume, handlesRecoveryCodes }: ConfirmChangeOptions
    ) =>
      sendWithConfirmation(
        change,
        (confirmation) =>
          new Promise<boolean>((resolve) => {
            answerRef.current?.(false);
            answerRef.current = resolve;
            setQuestion({
              label,
              methods: confirmation.methods,
              twoFactor: confirmation.twoFactor,
              resume,
              handlesRecoveryCodes,
            });
            setOpen(true);
          }),
        { precheck, getConfirmation }
      ),
    [getConfirmation]
  );

  // A refusal goes back to the modal as the API gave it, to be worded and
  // said under the field it came under. A recovery code that confirmed is
  // spent, and replaced, so the saved list has changed.
  const handleConfirmPassword = useCallback(
    async (proof: PasswordConfirmationProof): Promise<void> => {
      await confirmPassword(proof);
      if (proof.recovery_code && !question.handlesRecoveryCodes) {
        onRecoveryCodeSpent?.();
      }
      answer(true);
    },
    [answer, confirmPassword, onRecoveryCodeSpent, question]
  );

  // The page leaves for the provider, taking the change along. The question
  // stays open until the page is gone.
  const handleConfirmWithProvider = useCallback(
    (provider: SocialProvider): void => {
      const { label, resume } = question;
      leaveToConfirm(
        resume ? { provider, label, resume } : { provider, label }
      );
    },
    [leaveToConfirm, question]
  );

  // How each kind of change resumes after a provider's round trip.
  const resumes = useRef(new Map<string, ResumeChange>());

  const registerResume = useCallback(
    (kind: string, run: ResumeChange): (() => void) => {
      resumes.current.set(kind, run);
      return () => {
        if (resumes.current.get(kind) === run) resumes.current.delete(kind);
      };
    },
    []
  );

  const canResume = useCallback(
    (kind: string): boolean => resumes.current.has(kind),
    []
  );

  const resume = useCallback((change: ResumableChange): boolean => {
    const run = resumes.current.get(change.kind);
    if (!run) return false;
    run(change.params);
    return true;
  }, []);

  const value = useMemo(
    () => ({
      withConfirmation,
      registerResume,
      canResume,
      resume,
      leaveToConfirm,
    }),
    [withConfirmation, registerResume, canResume, resume, leaveToConfirm]
  );

  return (
    <IdentityConfirmationContext.Provider value={value}>
      {children}
      <ConfirmIdentityModal
        isOpen={open}
        changeLabel={question.label}
        methods={question.methods}
        twoFactor={question.twoFactor}
        showProviders={showProviders}
        onConfirmPassword={handleConfirmPassword}
        onConfirmWithProvider={handleConfirmWithProvider}
        onClose={() => answer(false)}
      />
    </IdentityConfirmationContext.Provider>
  );
}

/** "Confirm it's you, then carry on", from `IdentityConfirmationProvider`. */
export function useIdentityConfirmation(): IdentityConfirmationContextValue {
  const context = useContext(IdentityConfirmationContext);
  if (!context) {
    throw new Error(
      'useIdentityConfirmation must be used within an IdentityConfirmationProvider'
    );
  }
  return context;
}

/**
 * Registers how a kind of change resumes, for as long as the caller is
 * mounted: Settings runs it when a provider confirms the owner and the page
 * comes back with that change kept.
 */
export function useResumableChange(kind: string, run: ResumeChange): void {
  const { registerResume } = useIdentityConfirmation();
  // The latest `run`, so a re-render never re-registers.
  const latest = useRef(run);
  useEffect(() => {
    latest.current = run;
  });
  useEffect(
    () => registerResume(kind, (params) => latest.current(params)),
    [kind, registerResume]
  );
}

export default IdentityConfirmationProvider;
